"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

export interface AccommodationForm {
  id: string;
  patientId: string;
  episodeOfCareId?: string;
  status: "DRAFT" | "SUBMITTED" | "SIGNED";
  formData: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  signedAt?: string;
  createdBy?: string;
}

interface CreateAccommodationFormInput {
  patientId: string;
  episodeOfCareId?: string;
  formData: string;
}

interface CreateAccommodationFormMutationVariables {
  input: CreateAccommodationFormInput;
}

export const createAccommodationFormMutation = gql`
  mutation createAccommodationForm($input: CreateAccommodationFormInput!) {
    createAccommodationForm(input: $input) {
      id
      patientId
      episodeOfCareId
      status
      formData
      createdAt
      updatedAt
      submittedAt
      signedAt
      createdBy
    }
  }
`.loc?.source.body as GeneratedMutation<
  CreateAccommodationFormMutationVariables,
  {
    createAccommodationForm: AccommodationForm;
  }
>;

export const useCreateAccommodationForm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateAccommodationFormMutationVariables) =>
      fetchGraphQLClient(createAccommodationFormMutation, variables),
    onSuccess: (data) => {
      const form = data.data?.createAccommodationForm;
      if (form) {
        queryClient.invalidateQueries({
          queryKey: ["accommodationForms", form.patientId],
        });
        queryClient.invalidateQueries({
          queryKey: ["accommodationFormsByEpisode", form.episodeOfCareId],
        });
      }
    },
  });
};
